/* eslint-disable complexity */
// avoiding package root import to avoid circular dependency
// eslint-disable-next-line monorepo/no-internal-import
import {Locale} from '@aboutyou/config/src/locale';
// eslint-disable-next-line monorepo/no-internal-import
import {assertUnreachable} from '@aboutyou/utils/src/assertUnreachable';
import {DeprecatedRouteKeys, RouteKey} from './routeKeys';

/**
 * Manage the list of deprecated static routes
 * These routes are used to redirect the users back to the correct route
 *
 * @example
 * /kasse -> /checkout
 * */
export function getDeprecatedStaticRoutesByLocale_UNSAFE(
  locale: Locale,
): Record<DeprecatedRouteKeys, string> {
  switch (locale) {
    case Locale.de_AT:
    case Locale.de_CH:
    case Locale.de_DE:
      return {
        [RouteKey.Checkout]: 'kasse',
        [RouteKey.Search]: 'suche',
      };

    case Locale.fr_BE:
    case Locale.fr_CH:
    case Locale.fr_FR:
    case Locale.fr_LU:
      return {
        [RouteKey.Checkout]: 'finaliser-la-commande',
        [RouteKey.Search]: 'recherche',
      };

    case Locale.nl_BE:
    case Locale.nl_NL:
      return {
        [RouteKey.Checkout]: 'kassa',
        [RouteKey.Search]: 'zoeken',
      };

    case Locale.pl_PL:
      return {
        [RouteKey.Checkout]: 'kasa',
        [RouteKey.Search]: 'szukaj',
      };

    case Locale.cs_CZ:
      return {
        [RouteKey.Checkout]: 'pokladna',
        [RouteKey.Search]: 'hledani',
      };

    case Locale.sk_SK:
      return {
        [RouteKey.Checkout]: 'pokladna',
        [RouteKey.Search]: 'hladanie',
      };

    case Locale.ro_RO:
      return {
        [RouteKey.Checkout]: 'casa',
        [RouteKey.Search]: 'cautare',
      };

    case Locale.hu_HU:
      return {
        [RouteKey.Checkout]: 'kassza',
        [RouteKey.Search]: 'kereses',
      };

    case Locale.sl_SI:
      return {
        [RouteKey.Checkout]: 'blagajna',
        [RouteKey.Search]: 'iskanje',
      };

    case Locale.lv_LV:
      return {
        [RouteKey.Checkout]: 'kase',
        [RouteKey.Search]: 'meklet',
      };

    case Locale.lt_LT:
      return {
        [RouteKey.Checkout]: 'apmoketi',
        [RouteKey.Search]: 'ieskoti',
      };

    case Locale.et_EE:
      return {
        [RouteKey.Checkout]: 'kassa',
        [RouteKey.Search]: 'otsi',
      };

    case Locale.hr_HR:
      return {
        [RouteKey.Checkout]: 'blagajna',
        [RouteKey.Search]: 'pretrazi',
      };

    case Locale.sv_SE:
      return {
        [RouteKey.Checkout]: 'kassan',
        [RouteKey.Search]: 'search',
      };

    case Locale.es_ZZ:
    case Locale.es_ES:
      return {
        [RouteKey.Checkout]: 'caja',
        [RouteKey.Search]: 'busqueda',
      };

    case Locale.it_CH:
    case Locale.it_IT:
      return {
        [RouteKey.Checkout]: 'cassa',
        [RouteKey.Search]: 'ricerca',
      };

    case Locale.pt_PT:
      return {
        [RouteKey.Checkout]: 'caixa',
        [RouteKey.Search]: 'procurar',
      };
    case Locale.el_CY:
    case Locale.el_GR:
      return {
        [RouteKey.Checkout]: 'ταμείο',
        [RouteKey.Search]: 'search',
      };

    case Locale.no_NO:
      return {
        [RouteKey.Checkout]: 'kasse',
        [RouteKey.Search]: 'sok',
      };

    case Locale.bg_BG:
      return {
        [RouteKey.Checkout]: 'checkout',
        [RouteKey.Search]: 'search',
      };

    case Locale.da_DK:
      return {
        [RouteKey.Checkout]: 'checkout',
        [RouteKey.Search]: 'search',
      };

    case Locale.en_ZZ:
    case Locale.en_IE:
    case Locale.en_DE:
    case Locale.ar_SA:
    case Locale.en_SA:
    case Locale.he_IL:
    case Locale.en_IL:
      return {
        [RouteKey.Checkout]: 'checkout',
        [RouteKey.Search]: 'search',
      };

    case Locale.fi_FI:
      return {
        [RouteKey.Checkout]: 'checkout',
        [RouteKey.Search]: 'search',
      };
    case Locale.ru_EE:
    case Locale.ru_LV:
      return {
        [RouteKey.Checkout]: 'checkout',
        [RouteKey.Search]: 'search',
      };
    default:
      assertUnreachable(locale);
  }
}

export function getStaticRoutesByLocale_UNSAFE(
  locale: Locale,
): Record<Exclude<RouteKey, DeprecatedRouteKeys>, string> {
  switch (locale) {
    case Locale.de_AT:
    case Locale.de_CH:
    case Locale.de_DE:
      return {
        [RouteKey.YourShop]: 'dein-shop',
        [RouteKey.Basket]: 'warenkorb',
        [RouteKey.Wishlist]: 'wunschliste',
        [RouteKey.Brands]: 'marke',
        [RouteKey.Imprint]: 'impressum',
      };

    case Locale.fr_BE:
    case Locale.fr_CH:
    case Locale.fr_FR:
    case Locale.fr_LU:
      return {
        [RouteKey.YourShop]: 'votre-boutique',
        [RouteKey.Basket]: 'panier',
        [RouteKey.Wishlist]: 'liste-d-envies',
        [RouteKey.Brands]: 'marque',
        [RouteKey.Imprint]: 'mentions-legales',
      };

    case Locale.nl_BE:
    case Locale.nl_NL:
      return {
        [RouteKey.YourShop]: 'jouw-shop',
        [RouteKey.Basket]: 'winkelmandje',
        [RouteKey.Wishlist]: 'verlanglijstje',
        [RouteKey.Brands]: 'merk',
        [RouteKey.Imprint]: 'colofon',
      };

    case Locale.pl_PL:
      return {
        [RouteKey.YourShop]: 'twoj-sklep',
        [RouteKey.Basket]: 'koszyk',
        [RouteKey.Wishlist]: 'lista-zyczen',
        [RouteKey.Brands]: 'marka',
        [RouteKey.Imprint]: 'imprint',
      };

    case Locale.cs_CZ:
      return {
        [RouteKey.YourShop]: 'vas-obchod',
        [RouteKey.Basket]: 'nakupni-kosik',
        [RouteKey.Wishlist]: 'seznam-prani',
        [RouteKey.Brands]: 'znacka',
        [RouteKey.Imprint]: 'impresum',
      };

    case Locale.sk_SK:
      return {
        [RouteKey.YourShop]: 'tvoj-obchod',
        [RouteKey.Basket]: 'nakupny-kosik',
        [RouteKey.Wishlist]: 'zoznam-priani',
        [RouteKey.Brands]: 'znacka',
        [RouteKey.Imprint]: 'impresum',
      };

    case Locale.ro_RO:
      return {
        [RouteKey.YourShop]: 'magazinul-tau',
        [RouteKey.Basket]: 'cos-cumparaturi',
        [RouteKey.Wishlist]: 'lista-favorite',
        [RouteKey.Brands]: 'marca',
        [RouteKey.Imprint]: 'date-juridice',
      };

    case Locale.hu_HU:
      return {
        [RouteKey.YourShop]: 'boltod',
        [RouteKey.Basket]: 'kosar',
        [RouteKey.Wishlist]: 'kivansaglista',
        [RouteKey.Brands]: 'marka',
        [RouteKey.Imprint]: 'impresszum',
      };

    case Locale.sl_SI:
      return {
        [RouteKey.YourShop]: 'tvoj-nakup',
        [RouteKey.Basket]: 'kosarica',
        [RouteKey.Wishlist]: 'seznam-zelja',
        [RouteKey.Brands]: 'blagovne-znamke',
        [RouteKey.Imprint]: 'osnovni-podatki',
      };

    case Locale.lv_LV:
      return {
        [RouteKey.YourShop]: 'jusu-veikals',
        [RouteKey.Basket]: 'grozs',
        [RouteKey.Wishlist]: 'velmju-saraksts',
        [RouteKey.Brands]: 'zimols',
        [RouteKey.Imprint]: 'nospiedums',
      };

    case Locale.lt_LT:
      return {
        [RouteKey.YourShop]: 'mano-parduotuve',
        [RouteKey.Basket]: 'krepselis',
        [RouteKey.Wishlist]: 'megstamiausios',
        [RouteKey.Brands]: 'prekes-zenklas',
        [RouteKey.Imprint]: 'rekvizitai',
      };

    case Locale.et_EE:
      return {
        [RouteKey.YourShop]: 'minupood',
        [RouteKey.Basket]: 'ostukorv',
        [RouteKey.Wishlist]: 'soovinimekiri',
        [RouteKey.Brands]: 'brand',
        [RouteKey.Imprint]: 'impressum',
      };

    case Locale.hr_HR:
      return {
        [RouteKey.YourShop]: 'tvoj-shop',
        [RouteKey.Basket]: 'kosarica',
        [RouteKey.Wishlist]: 'lista-zelja',
        [RouteKey.Brands]: 'modna-marka',
        [RouteKey.Imprint]: 'osnovne-informacije',
      };

    case Locale.bg_BG:
      return {
        [RouteKey.YourShop]: 'tvoyat-magazin',
        [RouteKey.Basket]: 'basket',
        [RouteKey.Wishlist]: 'wishlist',
        [RouteKey.Brands]: 'marki',
        [RouteKey.Imprint]: 'imprint',
      };

    case Locale.da_DK:
      return {
        [RouteKey.YourShop]: 'din-shop',
        [RouteKey.Basket]: 'basket',
        [RouteKey.Wishlist]: 'wishlist',
        [RouteKey.Brands]: 'brands',
        [RouteKey.Imprint]: 'imprint',
      };

    case Locale.en_ZZ:
    case Locale.en_IE:
    case Locale.en_DE:
    case Locale.ar_SA:
    case Locale.en_SA:
    case Locale.he_IL:
    case Locale.en_IL:
      return {
        [RouteKey.YourShop]: 'your-shop',
        [RouteKey.Basket]: 'basket',
        [RouteKey.Wishlist]: 'wishlist',
        [RouteKey.Brands]: 'brands',
        [RouteKey.Imprint]: 'imprint',
      };

    case Locale.fi_FI:
      return {
        [RouteKey.YourShop]: 'sinun-shopisi',
        [RouteKey.Basket]: 'basket',
        [RouteKey.Wishlist]: 'wishlist',
        [RouteKey.Brands]: 'brands',
        [RouteKey.Imprint]: 'imprint',
      };

    case Locale.sv_SE:
      return {
        [RouteKey.YourShop]: 'din-shop',
        [RouteKey.Basket]: 'basket',
        [RouteKey.Wishlist]: 'wishlist',
        [RouteKey.Brands]: 'brands',
        [RouteKey.Imprint]: 'imprint',
      };

    case Locale.es_ZZ:
    case Locale.es_ES:
      return {
        [RouteKey.YourShop]: 'su-tienda',
        [RouteKey.Basket]: 'cesta-de-la-compra',
        [RouteKey.Wishlist]: 'lista-de-deseos',
        [RouteKey.Brands]: 'marca',
        [RouteKey.Imprint]: 'aviso-legal',
      };

    case Locale.it_IT:
    case Locale.it_CH:
      return {
        [RouteKey.YourShop]: 'tuo-negozio',
        [RouteKey.Basket]: 'carrello',
        [RouteKey.Wishlist]: 'lista-desideri',
        [RouteKey.Brands]: 'brand',
        [RouteKey.Imprint]: 'informazioni-legali',
      };

    case Locale.pt_PT:
      return {
        [RouteKey.YourShop]: 'o-teu-shop',
        [RouteKey.Basket]: 'cesto',
        [RouteKey.Wishlist]: 'favoritos',
        [RouteKey.Brands]: 'marca',
        [RouteKey.Imprint]: 'aviso-legal',
      };

    case Locale.el_GR:
      return {
        [RouteKey.YourShop]: 'your-shop',
        [RouteKey.Basket]: 'basket',
        [RouteKey.Wishlist]: 'wishlist',
        [RouteKey.Brands]: 'marka',
        [RouteKey.Imprint]: 'imprint',
      };

    case Locale.no_NO:
      return {
        [RouteKey.YourShop]: 'din-butikk',
        [RouteKey.Basket]: 'handlekurv',
        [RouteKey.Wishlist]: 'onskeliste',
        [RouteKey.Brands]: 'merker',
        [RouteKey.Imprint]: 'kolofon',
      };
    case Locale.el_CY:
      return {
        [RouteKey.YourShop]: 'your-shop',
        [RouteKey.Basket]: 'basket',
        [RouteKey.Wishlist]: 'wishlist',
        [RouteKey.Brands]: 'marka',
        [RouteKey.Imprint]: 'imprint',
      };
    case Locale.ru_EE:
    case Locale.ru_LV:
      return {
        [RouteKey.YourShop]: 'vash-magazin',
        [RouteKey.Basket]: 'korzina',
        [RouteKey.Wishlist]: 'izbrannoe',
        [RouteKey.Brands]: 'brendy',
        [RouteKey.Imprint]: 'svedeniya',
      };
    default:
      assertUnreachable(locale);
  }
}
